/**
 * Runtime Orchestration: Unified Runtime Engine
 *
 * The central orchestration layer that coordinates all runtime components.
 * Provides the main entry point for runtime operations.
 *
 * @platform-infrastructure
 */

import { RuntimeContext, RuntimeExecutionId, CorrelationId } from '../contracts'
import { RuntimeContextManager } from '../context/manager'
import { RuntimeLogger, LogCategory } from '../logging/logger'
import { RuntimeErrorNormalizer } from '../errors/normalizer'
import { RuntimeRequestExecutor, RequestExecutionOptions, RequestExecutionResult } from '../requests/executor'
import { RuntimeTracer } from '../tracing/tracer'
import { RuntimeTelemetryCollector, TelemetryEventType } from '../telemetry/collector'

/**
 * Runtime Orchestration Options
 */
export interface RuntimeOrchestrationOptions {
  /** Enable tracing */
  enableTracing?: boolean

  /** Enable telemetry */
  enableTelemetry?: boolean

  /** Enable structured logging */
  enableLogging?: boolean

  /** Request execution options */
  requestOptions?: RequestExecutionOptions

  /** Custom metadata */
  metadata?: Record<string, any>
}

/**
 * Runtime Engine
 *
 * The unified runtime orchestration layer for the BPAL platform.
 * Coordinates execution context, logging, tracing, telemetry, and error handling.
 */
export class RuntimeEngine {
  private static initialized: boolean = false
  private static options: RuntimeOrchestrationOptions = {
    enableTracing: true,
    enableTelemetry: true,
    enableLogging: true,
  }

  /**
   * Initialize the runtime engine
   */
  static initialize(options: RuntimeOrchestrationOptions = {}): void {
    if (this.initialized) return

    this.options = { ...this.options, ...options }

    // Initialize telemetry collector with default handler
    if (this.options.enableTelemetry) {
      RuntimeTelemetryCollector.addHandler((event) => {
        // Default telemetry handler - could send to analytics service
        if (process.env.NODE_ENV === 'development') {
          console.log('[TELEMETRY]', event.type, event.name, event.properties)
        }
      })
    }

    // Initialize logger with development settings
    if (process.env.NODE_ENV === 'development') {
      RuntimeLogger.setLogLevel('debug' as any)
    }

    this.initialized = true

    // Log engine initialization
    RuntimeLogger.info(LogCategory.RUNTIME_EXECUTION, 'Runtime engine initialized', undefined, {
      options: this.options,
    })
  }

  /**
   * Execute an operation with full runtime orchestration
   */
  static async execute<T>(
    operation: string,
    executor: () => Promise<T>,
    options: RuntimeOrchestrationOptions = {}
  ): Promise<RequestExecutionResult<T>> {
    const opts = { ...this.options, ...options }

    // Create execution context
    const context = RuntimeContextManager.createContext(
      null, // actor
      null, // route
      'runtime',
      'orchestration',
      opts.metadata
    )

    // Start tracing if enabled
    let trace = null
    if (opts.enableTracing) {
      trace = RuntimeTracer.startTrace(
        context.executionId,
        context.correlationId,
        operation
      )
    }

    // Execute with request executor
    const result = await RuntimeRequestExecutor.execute(
      operation,
      executor,
      opts.requestOptions
    )

    // End tracing
    if (opts.enableTracing && trace) {
      RuntimeTracer.endTrace(context.executionId, result.success ? 'success' : 'failure')
    }

    // Collect telemetry
    if (opts.enableTelemetry) {
      RuntimeTelemetryCollector.collectExecution(result.execution)
    }

    return result
  }

  /**
   * Execute an HTTP request with full runtime orchestration
   */
  static async executeHttp<T>(
    method: string,
    url: string,
    executor: () => Promise<T>,
    options: RuntimeOrchestrationOptions = {}
  ): Promise<RequestExecutionResult<T>> {
    const opts = { ...this.options, ...options }

    // Create execution context
    const context = RuntimeContextManager.createContext(
      null, // actor
      url, // route
      'http',
      'gateway',
      opts.metadata
    )

    // Start tracing if enabled
    let trace = null
    if (opts.enableTracing) {
      trace = RuntimeTracer.startTrace(
        context.executionId,
        context.correlationId,
        `${method} ${url}`
      )
    }

    // Execute HTTP request
    const result = await RuntimeRequestExecutor.executeHttp(
      method,
      url,
      executor,
      opts.requestOptions
    )

    // End tracing
    if (opts.enableTracing && trace) {
      RuntimeTracer.endTrace(context.executionId, result.success ? 'success' : 'failure')
    }

    // Collect telemetry
    if (opts.enableTelemetry) {
      RuntimeTelemetryCollector.collectExecution(result.execution)
    }

    return result
  }

  /**
   * Create a child execution context
   */
  static createChildContext(
    parentContext: RuntimeContext,
    service?: string,
    domain?: string,
    metadata?: Record<string, any>
  ): RuntimeContext {
    return RuntimeContextManager.createChildContext(parentContext, service, domain, metadata)
  }

  /**
   * Execute in a specific context
   */
  static async executeInContext<T>(
    context: RuntimeContext,
    executor: () => Promise<T>
  ): Promise<T> {
    return RuntimeContextManager.executeInContext(context, executor)
  }

  /**
   * Get current execution context
   */
  static getCurrentContext(): RuntimeContext | null {
    return RuntimeContextManager.getCurrentContext()
  }

  /**
   * Log a structured event
   */
  static log(
    category: LogCategory,
    message: string,
    data?: Record<string, any>,
    error?: Error
  ): void {
    if (!this.options.enableLogging) return

    const context = RuntimeContextManager.getCurrentContext()
    RuntimeLogger.info(category, message, context, data)

    if (error) {
      RuntimeLogger.error(category, `Error in ${message}`, context, data, error)
    }
  }

  /**
   * Collect telemetry event
   */
  static telemetry(
    type: TelemetryEventType,
    name: string,
    properties?: Record<string, any>,
    metrics?: Record<string, number>
  ): void {
    if (!this.options.enableTelemetry) return

    const context = RuntimeContextManager.getCurrentContext()
    RuntimeTelemetryCollector.collect(type, name, context, properties, metrics)
  }

  /**
   * Trace an event
   */
  static trace(
    executionId: RuntimeExecutionId,
    message: string,
    data?: Record<string, any>
  ): void {
    if (!this.options.enableTracing) return

    RuntimeTracer.traceProgress(executionId, message, data)
  }

  /**
   * Normalize an error
   */
  static normalizeError(error: any, context?: Record<string, any>): any {
    return RuntimeErrorNormalizer.normalize(error, context)
  }

  /**
   * Get runtime statistics
   */
  static getStats(): {
    initialized: boolean
    contextDepth: number
    activeTraces: number
    telemetryStats: any
    loggerLevel: string
  } {
    return {
      initialized: this.initialized,
      contextDepth: RuntimeContextManager.getExecutionDepth(),
      activeTraces: RuntimeTracer.getActiveTraces().length,
      telemetryStats: RuntimeTelemetryCollector.getStats(),
      loggerLevel: 'info', // Could expose from logger
    }
  }

  /**
   * Shutdown the runtime engine
   */
  static shutdown(): void {
    RuntimeTelemetryCollector.forceFlush()
    this.initialized = false

    RuntimeLogger.info(LogCategory.RUNTIME_EXECUTION, 'Runtime engine shutdown')
  }
}